import { useCallback, useEffect, useState } from "react";
import useCreatorStudioApi from "../api/use-creator-studio-api";
import { BrandDetails } from "../types/add-brand-types";
import { isEmpty } from "../utils/type-utils";
import {
  ContentFocus,
  ContentMode,
  CreatorStudioMode,
  DraftVariations,
  GenerateCampaignResponse,
} from "../types/creator-studio";
import {
  getCreatorStudioTemplateById,
  getCreatorStudioTemplatesForMode,
} from "../constants/creator-studio-templates";
import { trackEvent } from "../utils/firebase-analytics";

import { useCurrentUser } from "./useCurrentUser";
import { trackFirstValue } from "../utils/signup-analytics";

export const useCreatorStudio = (brandDetails: BrandDetails) => {
  const { generateCampaign } = useCreatorStudioApi();
  const { user } = useCurrentUser();
  const [step, setStep] = useState(1);
  const [generatedCampaign, setGeneratedCampaign] =
    useState<GenerateCampaignResponse | null>(null);
  const [step3BackStep, setStep3BackStep] = useState(2);
  const [prompt, setPrompt] = useState<string>("");
  const [additionalInstructions, setAdditionalInstructions] =
    useState<string>("");
  const [regenerationFeedback, setRegenerationFeedback] = useState<string>("");
  const [regenerationFeedbackHistory, setRegenerationFeedbackHistory] =
    useState<string[]>([]);
  const [selectedTone, setSelectedTone] = useState<string>(
    brandDetails.marketing_strategy?.tone || "Professional"
  );
  const [creatorMode, setCreatorMode] = useState<CreatorStudioMode>(
    CreatorStudioMode.SOCIAL_POST
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [selectedKnowledge, setSelectedKnowledge] = useState<string[]>([]);
  const [contentFocus, setContentFocus] = useState<ContentFocus>(
    ContentFocus.ABOUT_PRODUCT
  );
  const [selectedVariation, setSelectedVariation] = useState<DraftVariations>();
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info" | "warning";
    id: number;
  } | null>(null);

  const showToast = useCallback(
    (message: string, type: "success" | "error" | "info" | "warning") => {
      setToast({ message, type, id: Date.now() });
    },
    []
  );

  const closeToast = useCallback(() => {
    setToast(null);
  }, []);

  useEffect(() => {
    setSelectedTone(brandDetails.marketing_strategy?.tone || "Professional");
    setRegenerationFeedback("");
    setRegenerationFeedbackHistory([]);
  }, [brandDetails.id, brandDetails.marketing_strategy?.tone]);

  useEffect(() => {
    if (selectedTemplateId == null) {
      return;
    }
    const isSupported = getCreatorStudioTemplatesForMode(creatorMode).some(
      (template) => template.id === selectedTemplateId
    );
    if (!isSupported) {
      setSelectedTemplateId(null);
    }
  }, [creatorMode, selectedTemplateId]);

  const formatFeedbackHistory = useCallback((feedbackItems: string[]) => {
    return feedbackItems
      .map((feedback, index) => `${index + 1}. ${feedback}`)
      .join("\n");
  }, []);

  const runGeneration = useCallback(async (feedback = "") => {
    if (isEmpty(prompt)) {
      showToast("Please enter a topic", "warning");
      return false;
    }

    setIsLoading(true);
    trackEvent("content_generation_started", {
      mode: creatorMode,
      focus: contentFocus,
      knowledge_source_count: selectedKnowledge.length,
      has_additional_instructions: !isEmpty(additionalInstructions),
      has_template: Boolean(selectedTemplateId),
      is_regeneration: Boolean(feedback),
    });
    const generationMode =
      creatorMode === CreatorStudioMode.BLOG_PROMOTION
        ? ContentMode.BLOG_PROMOTION
        : ContentMode.SOCIAL;
    const generationFocus =
      creatorMode === CreatorStudioMode.BLOG_PROMOTION
        ? ContentFocus.ABOUT_PRODUCT
        : contentFocus;

    const selectedTemplate = getCreatorStudioTemplateById(selectedTemplateId);

    const response = await generateCampaign(
      prompt,
      brandDetails.id,
      selectedKnowledge,
      generationMode,
      generationFocus,
      {
        additionalInstructions,
        regenerationFeedback: feedback,
        targetTone: selectedTone,
        templateId: selectedTemplate?.id,
        templateLabel: selectedTemplate?.label,
        templateInstruction: selectedTemplate?.instruction,
      }
    );
    setIsLoading(false);

    const hasDrafts = (response?.drafts?.length ?? 0) > 0;
    if (response?.status !== "success" || !hasDrafts) {
      trackEvent("content_generation_failed", {
        mode: creatorMode,
        focus: contentFocus,
        knowledge_source_count: selectedKnowledge.length,
        is_regeneration: Boolean(feedback),
      });
      setGeneratedCampaign(null);
      showToast(
        response?.status === "error" && response.research
          ? response.research
          : "An error occurred while generating content.",
        "error"
      );
      return false;
    }

    setGeneratedCampaign(response);
    if (user?.id) trackFirstValue(user.id, response.drafts?.length ?? 0);
    trackEvent("content_generation_completed", {
      mode: creatorMode,
      focus: contentFocus,
      draft_count: response.drafts?.length ?? 0,
      knowledge_source_count: selectedKnowledge.length,
      has_template: Boolean(selectedTemplateId),
      is_regeneration: Boolean(feedback),
    });
    setStep3BackStep(2);
    setStep(2);
    return true;
  }, [
    additionalInstructions,
    brandDetails.id,
    creatorMode,
    contentFocus,
    generateCampaign,
    prompt,
    selectedKnowledge,
    selectedTemplateId,
    selectedTone,
    showToast,
    user?.id,
  ]);

  const onGenerateCampaign = useCallback(async () => {
    await runGeneration(formatFeedbackHistory(regenerationFeedbackHistory));
  }, [formatFeedbackHistory, regenerationFeedbackHistory, runGeneration]);

  const onRegenerateWithFeedback = useCallback(async () => {
    const trimmedFeedback = regenerationFeedback.trim();
    if (isEmpty(trimmedFeedback)) {
      showToast("Please describe what should change", "warning");
      return;
    }

    const nextFeedbackHistory = [
      ...regenerationFeedbackHistory,
      trimmedFeedback,
    ];
    const wasGenerated = await runGeneration(
      formatFeedbackHistory(nextFeedbackHistory)
    );

    if (wasGenerated) {
      setRegenerationFeedbackHistory(nextFeedbackHistory);
      setRegenerationFeedback("");
    }
  }, [
    formatFeedbackHistory,
    regenerationFeedback,
    regenerationFeedbackHistory,
    runGeneration,
    showToast,
  ]);

  const onClearRegenerationFeedbackHistory = useCallback(() => {
    setRegenerationFeedback("");
    setRegenerationFeedbackHistory([]);
    showToast("Feedback memory cleared", "info");
  }, [showToast]);

  const handleBack = useCallback(() => {
    setStep(1);
  }, [setStep]);

  const goToOwnPost = useCallback(() => {
    setStep3BackStep(1);
    setStep(3);
  }, []);

  const handleStep3Back = useCallback(() => {
    setStep(step3BackStep);
  }, [step3BackStep]);

  const onVariationSelect = useCallback(
    (selectedVariation: DraftVariations) => {
      setSelectedVariation(selectedVariation);
      setStep3BackStep(2);
      setStep(3);
    },
    []
  );

  return {
    onGenerateCampaign,
    step,
    setStep,
    prompt,
    setPrompt,
    additionalInstructions,
    setAdditionalInstructions,
    regenerationFeedback,
    setRegenerationFeedback,
    regenerationFeedbackHistory,
    onRegenerateWithFeedback,
    onClearRegenerationFeedbackHistory,
    selectedTone,
    setSelectedTone,
    selectedTemplateId,
    setSelectedTemplateId,
    creatorMode,
    setCreatorMode,
    contentFocus,
    setContentFocus,
    isLoading,
    setIsLoading,
    handleBack,
    toast,
    closeToast,
    showToast,
    generatedCampaign,
    setSelectedKnowledge,
    onVariationSelect,
    selectedVariation,
    goToOwnPost,
    handleStep3Back,
  };
};

export default useCreatorStudio;
